import { Check, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import {
  Autocomplete,
  AutocompleteOption,
  ChipDelete,
  createFilterOptions,
  FormHelperText,
  FormLabel,
  ListItemContent,
  ListItemDecorator,
  Typography,
} from "@mui/joy";
import { ApiRouteLabelGroup } from "griptonite-api-client";
import { KeyboardEvent, useMemo, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import RouteLabelChip from "@/components/RouteLabels/RouteLabelChip";
import { buildLabelList, splitRouteLabelConfig } from "@/components/RouteLabels/routeLabelsConfig";
import { useRouteLabelConfigQuery } from "@/hooks/routeLabels";

import FormGroup from "./FormGroup";

type Entry =
  | { kind: "group"; uuid: string; group: ApiRouteLabelGroup }
  | { kind: "back"; uuid: string; group: ApiRouteLabelGroup }
  | { kind: "label"; uuid: string; name: string; group: ApiRouteLabelGroup | null };

const emptyArray: never[] = [];

const entryLabel = (entry: Entry) => {
  if (entry.kind !== "label") {
    return entry.group.name;
  }
  return entry.group ? `${entry.group.name}: ${entry.name}` : entry.name;
};

// Searching matches a grouped label by its group's name as well as its own.
const filter = createFilterOptions<Entry>({
  stringify: (entry) =>
    (entry.kind === "label" && entry.group ? entry.group.name : "") + " " + entryLabel(entry),
});

/**
 * Picks a route's labels. The field's value is a flat list of label uuids.
 *
 * The dropdown first lists groups and root labels together. Choosing a group
 * swaps the list for that group's options, with a row at the top to go back.
 * Typing searches every label, grouped ones included.
 */
export default function RouteLabelsInput({ name, label }: { name: string; label: string }) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control });
  const selected: string[] = field.value || emptyArray;

  const { data: config } = useRouteLabelConfigQuery();
  const options = useMemo(() => {
    const { activeGroups, activeRootLabels } = splitRouteLabelConfig(config);
    return buildLabelList(activeGroups, activeRootLabels).flatMap((item): Entry[] =>
      item.kind === "group"
        ? [
            { kind: "group", uuid: item.group.uuid, group: item.group },
            { kind: "back", uuid: `back:${item.group.uuid}`, group: item.group },
            ...item.group.labels.map((option): Entry => ({
              kind: "label",
              uuid: option.uuid,
              name: option.name,
              group: item.group,
            })),
          ]
        : [{ kind: "label", uuid: item.label.uuid, name: item.label.name, group: null }],
    );
  }, [config]);

  // The group whose options the dropdown is showing, if any.
  const [openGroupUuid, setOpenGroupUuid] = useState<string | null>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const value = options.filter((entry) => entry.kind === "label" && selectedSet.has(entry.uuid));

  if (!config?.groups.length && !config?.labels.length) {
    return null;
  }

  return (
    <FormGroup error={Boolean(fieldState.error)}>
      <FormLabel>{label}</FormLabel>
      <Autocomplete
        multiple
        disableCloseOnSelect
        placeholder={value.length ? undefined : "Add labels"}
        options={options}
        value={value}
        onChange={(_, newValue, _reason, details) => {
          const option = details?.option;
          // Groups and the back row only move around the list. They're never
          // part of the value.
          if (option?.kind === "group") {
            setOpenGroupUuid(option.group.uuid);
            return;
          }
          if (option?.kind === "back") {
            setOpenGroupUuid(null);
            return;
          }
          field.onChange(newValue.filter((entry) => entry.kind === "label").map((e) => e.uuid));
        }}
        onClose={() => setOpenGroupUuid(null)}
        onBlur={field.onBlur}
        // Backspace in an empty input goes back out of a group, rather than
        // removing the last chip.
        onKeyDown={(event: KeyboardEvent<HTMLDivElement> & { defaultMuiPrevented?: boolean }) => {
          const input = event.target as HTMLInputElement;
          if (openGroupUuid && event.key === "Backspace" && !input.value) {
            event.defaultMuiPrevented = true;
            setOpenGroupUuid(null);
          }
        }}
        getOptionLabel={entryLabel}
        getOptionKey={(entry) => entry.uuid}
        isOptionEqualToValue={(option, other) => option.uuid === other.uuid}
        filterOptions={(all, state) => {
          if (openGroupUuid) {
            const back = all.filter((e) => e.kind === "back" && e.group.uuid === openGroupUuid);
            const inGroup = all.filter(
              (e) => e.kind === "label" && e.group?.uuid === openGroupUuid,
            );
            return [...back, ...filter(inGroup, state)];
          }
          if (!state.inputValue) {
            return all.filter((e) => e.kind === "group" || (e.kind === "label" && !e.group));
          }
          return filter(
            all.filter((e) => e.kind !== "back"),
            state,
          );
        }}
        renderOption={(props, entry, { selected: isSelected }) => {
          if (entry.kind === "back") {
            return (
              <AutocompleteOption
                {...props}
                key={entry.uuid}
                sx={{ fontWeight: "lg", borderBottom: "1px solid", borderColor: "divider" }}
              >
                <ListItemDecorator>
                  <KeyboardArrowLeft />
                </ListItemDecorator>
                {entry.group.name}
              </AutocompleteOption>
            );
          }
          if (entry.kind === "group") {
            const chosen = entry.group.labels.filter((option) => selectedSet.has(option.uuid));
            return (
              <AutocompleteOption {...props} key={entry.uuid}>
                <ListItemContent>
                  {entry.group.name}
                  <Typography level="body-xs" noWrap>
                    {chosen.length
                      ? chosen.map((option) => option.name).join(", ")
                      : entry.group.labels.map((option) => option.name).join(" · ")}
                  </Typography>
                </ListItemContent>
                <KeyboardArrowRight />
              </AutocompleteOption>
            );
          }
          return (
            <AutocompleteOption {...props} key={entry.uuid}>
              <ListItemContent>
                {entry.name}
                {entry.group && !openGroupUuid ? (
                  <Typography level="body-xs">{entry.group.name}</Typography>
                ) : null}
              </ListItemContent>
              {/* On the right, where groups show their arrow, so every row
                  lines up on the left. */}
              {isSelected ? <Check /> : null}
            </AutocompleteOption>
          );
        }}
        renderTags={(tags, getTagProps) =>
          tags.map((entry, index) => {
            const { onClick, ...tagProps } = getTagProps({ index });
            return (
              <RouteLabelChip
                {...tagProps}
                key={entry.uuid}
                name={entry.kind === "label" ? entry.name : entryLabel(entry)}
                groupName={entry.kind === "label" ? entry.group?.name : null}
                endDecorator={<ChipDelete onDelete={onClick} />}
              />
            );
          })
        }
      />
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormGroup>
  );
}
